import React from 'react';
import { Helmet } from 'react-helmet-async';
import { flag } from '../config';
import { SITE_NAME, SITE_URL, serializeJsonLd } from '../lib/structuredData';

/**
 * SEO Component for dynamic meta tags
 *
 * Usage:
 * <SEO
 *   title="Page Title"
 *   description="Page description"
 *   image="/path/to/image.png"
 *   url="/page-path"
 *   type="website"
 *   jsonLd={{ '@context': 'https://schema.org', '@type': 'Organization', ... }}
 * />
 *
 * With SLOT_MODEL on (Phase 5d) the title is composed by composeTitle (the brand once:
 * "X | Tappd", or X alone when it already names Tappd) and the description is cut to
 * DESCRIPTION_MAX characters by trimDescription (meta, og and twitter alike). With the flag
 * off both are exactly as before ("X | Tappd", description as given), so production head
 * tags do not change until the flag does.
 *
 * `jsonLd` (object or array of objects) renders one <script type="application/ld+json">
 * (an array becomes one @graph; "<" escaped, see lib/structuredData serializeJsonLd).
 * `structuredData` is the older, unescaped variant kept for the PageSEO presets.
 */

export const DESCRIPTION_MAX = 155;

/**
 * A meta description of at most `max` characters: whitespace collapsed; when longer, cut at
 * the last word boundary that leaves room for "…", trailing punctuation dropped, "…" added.
 * A single word longer than the limit is cut hard. Characters are Unicode code points
 * (Array.from), so an emoji counts once and a cut never splits a surrogate pair.
 * Non-strings are returned unchanged.
 */
export function trimDescription(text, max = DESCRIPTION_MAX) {
  if (typeof text !== 'string') return text;
  const s = text.replace(/\s+/g, ' ').trim();
  const chars = Array.from(s);
  if (chars.length <= max) return s;
  const room = Math.max(1, max - 1); // one character for the ellipsis
  let cut = chars.slice(0, room);
  if (chars[room] !== ' ') {
    const space = cut.lastIndexOf(' ');
    if (space > 0) cut = cut.slice(0, space);
  }
  const kept = cut.join('').replace(/[\s,;:.!?\u2013\u2014-]+$/, '');
  return `${kept || chars.slice(0, room).join('')}\u2026`;
}

const escapeRegExp = (v) => v.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/**
 * "<title> | Tappd", with the brand once: a title that already names the brand (as a word,
 * any case, e.g. "How Tappd works" or "Our Story | Tappd") is used as it is. Empty -> null.
 */
export function composeTitle(title, brand = SITE_NAME) {
  const t = typeof title === 'string' ? title.replace(/\s+/g, ' ').trim() : '';
  if (!t) return null;
  if (new RegExp(`(^|[^A-Za-z0-9])${escapeRegExp(brand)}([^A-Za-z0-9]|$)`, 'i').test(t)) return t;
  return `${t} | ${brand}`;
}

const SEO = ({
  title,
  description,
  image,
  url,
  type = 'website',
  keywords,
  noIndex = false,
  structuredData,
  jsonLd
}) => {
  const siteUrl = SITE_URL;
  const defaultTitle = 'Tappd - QR Coaster Advertising';
  const defaultDescription = 'QR-enabled coaster advertising platform connecting local businesses with customers at bars and restaurants. Sip. Scan. Save.';
  const defaultImage = `${siteUrl}/tappd-og-image.png`;
  // Phase 5d head rules ride on SLOT_MODEL; flag off = the pre-5d output byte for byte.
  const slotSeo = flag('SLOT_MODEL');
  const plainTitle = title ? `${title} | Tappd` : defaultTitle;
  const plainDescription = description || defaultDescription;

  const seo = {
    title: slotSeo ? (composeTitle(title) || defaultTitle) : plainTitle,
    description: slotSeo ? trimDescription(plainDescription) : plainDescription,
    image: image ? (image.startsWith('http') ? image : `${siteUrl}${image}`) : defaultImage,
    url: url ? `${siteUrl}${url}` : siteUrl,
  };
  const ld = serializeJsonLd(jsonLd);

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{seo.title}</title>
      <meta name="title" content={seo.title} />
      <meta name="description" content={seo.description} />
      {keywords && <meta name="keywords" content={keywords} />}
      <link rel="canonical" href={seo.url} />
      {noIndex && <meta name="robots" content="noindex, nofollow" />}
      
      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={seo.url} />
      <meta property="og:title" content={seo.title} />
      <meta property="og:description" content={seo.description} />
      <meta property="og:image" content={seo.image} />
      <meta property="og:site_name" content="Tappd" />
      
      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={seo.url} />
      <meta name="twitter:title" content={seo.title} />
      <meta name="twitter:description" content={seo.description} />
      <meta name="twitter:image" content={seo.image} />
      
      {/* Structured Data */}
      {structuredData && (
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      )}
      {ld && <script type="application/ld+json">{ld}</script>}
    </Helmet>
  );
};

export default SEO;

/**
 * Pre-built SEO configurations for common pages
 */
export const PageSEO = {
  // `jsonLd` is forwarded so the page can add its structured data; the preset's own tags are unchanged.
  Home: ({ jsonLd } = {}) => (
    <SEO 
      title="QR Coaster Advertising | Turn Bar Seats Into Local Customers"
      description="Tappd places QR-enabled coasters in bars and restaurants, connecting local businesses with customers at the perfect moment. Affordable advertising that actually works."
      url="/"
      keywords="QR code advertising, coaster advertising, local marketing, bar advertising, restaurant marketing, small business advertising"
      jsonLd={jsonLd}
    />
  ),
  
  Pricing: () => (
    <SEO 
      title="Pricing"
      description="Two simple plans: Local ($125/month — one venue, one deal) and Multi ($199/month — up to three venues and three deals). Annual billing gets two months free."
      url="/pricing"
      keywords="advertising pricing, local advertising cost, small business marketing pricing, QR advertising plans"
      structuredData={{
        "@context": "https://schema.org",
        "@type": "PriceSpecification",
        "price": "125",
        "priceCurrency": "USD",
        "description": "Local plan: one partner venue, one active deal"
      }}
    />
  ),
  
  HowItWorks: () => (
    <SEO 
      title="How It Works"
      description="Learn how Tappd connects local businesses with customers through QR-enabled coasters at bars and restaurants. Simple, effective, and affordable advertising."
      url="/how-it-works"
      keywords="how QR advertising works, coaster advertising explained, local marketing solution"
    />
  ),
};
